import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Fuel, IndianRupee, FileText, MapPin, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const { data: cust } = useQuery({
    queryKey: ["me-customer"],
    queryFn: () => user?.customer_id ? api.get(`/customers/${user.customer_id}`).then((r) => r.data) : null,
    enabled: !!user?.customer_id,
  });
  const { data: orders } = useQuery({
    queryKey: ["me-orders"], queryFn: () => api.get("/orders?limit=20").then((r) => r.data.items), refetchInterval: 15000,
  });
  const { data: invoices } = useQuery({
    queryKey: ["me-invoices"], queryFn: () => api.get("/accounts/invoices").then((r) => r.data),
  });

  const active = (orders || []).filter((o) => !["delivered", "cancelled"].includes(o.status));
  const outstanding = (invoices?.items || []).reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Customer Portal</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Hello, {user?.name}</h1>
        </div>
        <Link to="/portal/book"><Button size="lg" className="pill-btn" data-testid="btn-book-diesel"><Fuel size={16} className="mr-2" /> Book diesel</Button></Link>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPI icon={Fuel} label="Active orders" value={active.length} />
        <KPI icon={FileText} label="Total invoices" value={invoices?.total || 0} />
        <KPI icon={IndianRupee} label="Outstanding" value={`₹${outstanding.toLocaleString("en-IN")}`} highlight />
        <KPI icon={MapPin} label="Sites" value={cust?.sites?.length || 0} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Live orders</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {active.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No active orders. <Link to="/portal/book" className="text-primary hover:underline">Book now →</Link></div>}
            {active.map((o) => (
              <Link to={`/portal/track/${o.id}`} key={o.id} className="block rounded-lg border border-border/60 p-3 transition-transform hover:-translate-y-0.5" data-testid={`active-order-${o.code}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm font-bold">{o.code}</div>
                    <div className="text-xs text-muted-foreground">{o.site_name} · {o.quantity_litres}L</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={o.status} />
                    <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Recent invoices</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-2">Invoice</th><th className="px-4 py-2">Date</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2">Status</th></tr>
              </thead>
              <tbody>
                {(invoices?.items || []).slice(0, 8).map((i) => (
                  <tr key={i.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-mono">{i.code}</td>
                    <td className="px-4 py-2 text-xs">{new Date(i.invoice_date).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">₹{i.total.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2"><StatusBadge status={i.payment_status} /></td>
                  </tr>
                ))}
                {(!invoices?.items || invoices.items.length === 0) && (<tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No invoices yet</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, highlight }) => (
  <div className="kpi-card">
    <div className="flex items-start justify-between gap-2 text-muted-foreground">
      <div className="min-w-0 text-xs font-semibold uppercase tracking-widest">{label}</div>
      <Icon size={16} className="mt-0.5 shrink-0" />
    </div>
    <div className={`mt-2 truncate font-display text-xl font-black sm:text-2xl ${highlight ? "text-primary" : ""}`} title={String(value)}>{value}</div>
  </div>
);
